import { Tooltip, type TooltipProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Rotate3d } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { OverlayLabel } from '~/components/OverlayLabel';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { petMipRowSeriesState } from '~/state/atoms/viewerAtoms';
import { OverlayActionButton } from './OverlayActionButton';

interface PetMipToggleButtonProps {
    /** The row's image series: the row the projection floats over. */
    readonly rowSeriesUid: SeriesInstanceUid;
    /** Which way the tooltip opens; never downward, over the next control. */
    readonly tooltipPlacement?: TooltipProps['placement'];
}

/** Toggles the PET MIP over one row; another row's toggle moves it there. */
export default function PetMipToggleButton({
    rowSeriesUid,
    tooltipPlacement = 'left',
}: PetMipToggleButtonProps) {
    const intl = useIntl();
    const [openRow, setOpenRow] = useRecoilState(petMipRowSeriesState);
    const isOpen = openRow === rowSeriesUid;
    const label = intl.formatMessage({ id: 'petMip.toggle' });
    return (
        <Tooltip title={label} placement={tooltipPlacement}>
            <OverlayActionButton
                focusRipple
                onClick={() => setOpenRow(isOpen ? null : rowSeriesUid)}
                aria-label={label}
                aria-pressed={isOpen}
                data-testid="pet-mip-toggle"
                sx={(theme: Theme) => ({
                    ...theme.mixins.glassOverlay,
                    mr: -1.5,
                })}
            >
                <Rotate3d size={12} aria-hidden />
                <OverlayLabel>
                    {intl.formatMessage({ id: 'petMip.title' })}
                </OverlayLabel>
            </OverlayActionButton>
        </Tooltip>
    );
}
